import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const FAQ_SECTIONS = [
  {
    id: 'general',
    title: 'General Questions',
    icon: 'fa-question-circle',
    items: [
      {
        q: 'What is PPSR?',
        a: `PPSR stands for Personal Property Securities Register. It is Australia's national online register where security interests in personal property (including vehicles) are registered. It helps buyers verify whether a vehicle has any outstanding finance or security interests registered against it.`,
      },
      {
        q: 'Why should I check a vehicle on the PPSR?',
        a: `Checking a vehicle on the PPSR is critical before purchasing any second-hand vehicle. It reveals if there is any money owing (finance), if the vehicle has been reported stolen, if it has been written off, and any other security interests. Without a PPSR check you could unknowingly buy someone else's debt.`,
      },
      {
        q: 'What vehicles can I check?',
        a: 'You can check any registered motor vehicle in Australia including cars, motorcycles, trucks, caravans, boats and trailers. You can search by registration plate number (and state) or by VIN (Vehicle Identification Number).',
      },
      {
        q: 'How quickly will I receive my report?',
        a: 'Reports are instant. Once your payment is processed you will be redirected to your report within seconds. A copy is also sent to your email address.',
      },
    ],
  },
  {
    id: 'reports',
    title: 'About Reports',
    icon: 'fa-file-alt',
    items: [
      {
        q: 'What is the difference between Basic and Comprehensive?',
        a: `The Basic PPSR Check covers the core PPSR data: finance, stolen, written-off, security interests and VIN validation. The Comprehensive Report includes everything in the Basic plus odometer history, registration timeline, market valuation guide, and first sale information.`,
      },
      {
        q: 'Is the report an official government document?',
        a: 'Our reports are generated directly from data held on the official government PPSR database. The certificate includes a unique Certificate ID and timestamp confirming when the search was conducted.',
      },
      {
        q: 'Can I download the report as a PDF?',
        a: 'Yes. Once your report is generated you can download it as a PDF directly from the report page. A copy is also emailed to the address you provide at checkout.',
      },
      {
        q: 'How long is the report valid?',
        a: 'A PPSR search reflects the state of the register at the moment of the search. The data is current as of the search date and time. We recommend conducting a new search if you are about to purchase a vehicle, even if you have a recent report.',
      },
    ],
  },
  {
    id: 'payment',
    title: 'Payment & Pricing',
    icon: 'fa-credit-card',
    items: [
      {
        q: 'What payment methods are accepted?',
        a: `We accept all major credit and debit cards (Visa, Mastercard, American Express) via Stripe's secure payment platform. All payments are processed in Australian Dollars (AUD).`,
      },
      {
        q: 'Is my payment information secure?',
        a: 'Yes. We use Stripe — a PCI DSS Level 1 certified payment processor. Your card details are never transmitted to or stored on our servers. All transactions are secured with 256-bit SSL encryption.',
      },
      {
        q: 'Do you offer refunds?',
        a: 'As reports are instantly generated from a live government database, we are unable to offer refunds once a report has been delivered. If you believe there is an error, please contact our support team and we will do our best to assist.',
      },
    ],
  },
  {
    id: 'technical',
    title: 'Technical Support',
    icon: 'fa-tools',
    items: [
      {
        q: "The PDF won't download. What should I try?",
        a: 'Try using a different browser (Chrome or Firefox are recommended). If you are on a corporate network a firewall may be blocking the download — try from a personal device. If the issue persists, contact our support team.',
      },
      {
        q: 'I entered the wrong registration number. Can I get a new report?',
        a: 'Unfortunately each purchase is for a specific search. If you entered the wrong details you will need to purchase a new report. Please double-check your registration number and state before completing payment.',
      },
      {
        q: 'What browsers are supported?',
        a: 'Our website supports all modern browsers: Chrome, Firefox, Safari, Edge and Opera. We recommend keeping your browser updated to the latest version for the best experience.',
      },
    ],
  },
];

function FaqItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item${open ? ' faq-open' : ''}`}>
      <button className="faq-question" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span>{item.q}</span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'}`}></i>
      </button>
      {open && <div className="faq-answer"><p>{item.a}</p></div>}
    </div>
  );
}

export default function Help() {
  const [activeSection, setActiveSection] = useState('general');

  const current = FAQ_SECTIONS.find((s) => s.id === activeSection);

  return (
    <>
      <Header />

      <section className="page-header">
        <div className="container">
          <h1>Help &amp; FAQ</h1>
          <p>Find answers to the most common questions about our PPSR reports and payment process.</p>
        </div>
      </section>

      <section className="help-section">
        <div className="container">
          <div className="help-grid">
            {/* Sidebar */}
            <aside className="help-sidebar">
              <nav className="help-nav">
                {FAQ_SECTIONS.map((s) => (
                  <button
                    key={s.id}
                    className={`help-nav-item${activeSection === s.id ? ' active' : ''}`}
                    onClick={() => setActiveSection(s.id)}
                  >
                    <i className={`fas ${s.icon}`}></i>
                    {s.title}
                  </button>
                ))}
              </nav>
            </aside>

            {/* FAQ Content */}
            <div className="help-content">
              {current && (
                <>
                  <h2 className="help-section-title">
                    <i className={`fas ${current.icon}`}></i> {current.title}
                  </h2>
                  <div className="faq-list">
                    {current.items.map((item, i) => (
                      <FaqItem key={i} item={item} />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Contact CTA */}
          <div className="help-contact-cta">
            <h3>Still need help?</h3>
            <p>Our support team is available Monday–Friday, 8:30am–6pm AEST.</p>
            {/* <div className="help-contact-methods">
              <div className="contact-method">
                <i className="fas fa-phone"></i>
                <div>
                  <strong>Phone</strong>
                  <span>1300 799 523</span>
                </div>
              </div>
              <div className="contact-method">
                <i className="fas fa-envelope"></i>
                <div>
                  <strong>Email</strong>
                  <span>info@ppsr-services.com.au</span>
                </div>
              </div>
            </div>*/} 
            <Link to="/contact" className="btn btn-primary">Contact Us</Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
